/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useState } from "react";
import useForm from "react-hook-form";
import {
  emailValidator,
  authCheckCallApi,
  refreshToken
} from "../../Authenticator/utils";
import {
  Form,
  Button,
  Dimmer,
  Loader,
  Modal,
  Transition,
  Grid,
} from "semantic-ui-react";
import styles from "../MyAccount.module.css";
import cStyles from "../../Components.module.css";
import { ValidationError } from "../../Authenticator";
import { useMutation } from "@apollo/react-hooks";
import { ChangeEmailMutation } from "../../../graphql/user";
import { MessageDialogParams } from "../../../types/MessageDialogParams";
import { Ico } from "../../Icon/Icon";
import ConfirmationDialog from "../../ConfirmationDialog";
import { useTranslation } from "react-i18next";

interface ChangeEmailDialogProps {
  currentEmail: string;
  userId: string;
  onChangeEmailComplete: any;
  disableTokenRefresh?: boolean;
}

const ChangeEmailDialog: React.FC<ChangeEmailDialogProps> = ({
  currentEmail,
  userId,
  onChangeEmailComplete,
  disableTokenRefresh,
}) => {
  type FormData = {
    username: string;
    confirmUsername: string;
  };

  const { t } = useTranslation(["changeEmail", "common"]);

  const emptyInputs = {
    username: "",
    confirmUsername: "",
  };
  const [inputs, setInputs] = useState<FormData>(emptyInputs);
  const { register, handleSubmit, errors, getValues } = useForm<FormData>();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<MessageDialogParams | null>(null);
  const [emailChangeSuccess, setEmailChangeSuccess] = useState<boolean>(false);
  const [changeEmail] = useMutation(ChangeEmailMutation, {
    onError: () => {
      setLoading(false);
      if (!message) {
        setMessage({
          title: t("change"),
          message: t("change_error"),
        });
      }
    },
    onCompleted: () => {
      setEmailChangeSuccess(true);
      if (!disableTokenRefresh) {
        refreshToken(() => {
          // noop
        });
      }
      setLoading(false);
      setMessage({
        title: t("change"),
        message: t("change_ok"),
      });
    },
  });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const onChangeEmail = () => {
    setLoading(true);
    authCheckCallApi(changeEmail, {
      variables: {
        userId: userId,
        newEmail: inputs.username.toLocaleLowerCase(),
      },
    });
  };

  return (
    <Transition animation="fade in" duration="250">
      <Modal
        size="small"
        open
        onClose={() => onChangeEmailComplete()}
        closeOnEscape={true}
        closeOnDimmerClick={false}
      >
        {loading && (
          <Dimmer active={true}>
            <Loader size="large" active={true} />
          </Dimmer>
        )}

        <Form onSubmit={handleSubmit(onChangeEmail)}>
          <input type="submit" hidden />
          <Grid stackable padded style={{ padding: "15px" }}>
            <Grid.Row>
              <Grid.Column width={6}>
                <span className={cStyles.title}>{t("change")}</span>
              </Grid.Column>

              <Grid.Column width={6} floated="right">
                <div
                  className={cStyles.closeButton}
                  onClick={() => onChangeEmailComplete()}
                >
                  <Ico name="cancel" style={{ color: "#ffffff", margin: 0 }} />
                </div>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={4} textAlign="right" verticalAlign="middle">
                <label>{t("current")}</label>
              </Grid.Column>
              <Grid.Column
                width={10}
                textAlign="left"
                style={{
                  lineHeight: "normal",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
              >
                {currentEmail}
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={4} textAlign="right" verticalAlign="middle">
                <label>{t("new")}</label>
              </Grid.Column>
              <Grid.Column width={12} textAlign="left" stretched>
                <Form.Field required error={!!errors.username}>
                  <input
                    autoFocus
                    name="username"
                    key="username"
                    className={styles.myAccountInput}
                    placeholder={t("enter_new")}
                    autoComplete="email"
                    ref={register({
                      pattern: emailValidator,
                      validate: (value) =>
                        value.toLocaleLowerCase() !==
                          currentEmail.toLocaleLowerCase() ||
                        (t("must_be_different") as string),
                    })}
                    onChange={handleInputChange}
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row style={{ padding: 0 }}>
              <Grid.Column
                width={4}
                textAlign="right"
                verticalAlign="middle"
              ></Grid.Column>
              <Grid.Column width={12} textAlign="left" stretched>
                <ValidationError
                  errors={errors}
                  name="username"
                  pointing="above"
                  internal={true}
                />
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={4} textAlign="right" verticalAlign="middle">
                <label>{t("confirm")}</label>
              </Grid.Column>
              <Grid.Column width={12} textAlign="left" stretched>
                <Form.Field required error={!!errors.confirmUsername}>
                  <input
                    name="confirmUsername"
                    key="confirmUsername"
                    className={styles.myAccountInput}
                    placeholder={t("confirm_new")}
                    autoComplete="off"
                    ref={register({
                      validate: (value) =>
                        value.toLocaleLowerCase() ===
                          getValues().username.toLocaleLowerCase() ||
                        (t("no_match") as string),
                    })}
                    onChange={handleInputChange}
                    onPaste={(e) => {
                      e.preventDefault();
                    }}
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row style={{ padding: 0 }}>
              <Grid.Column
                width={4}
                textAlign="right"
                verticalAlign="middle"
              ></Grid.Column>
              <Grid.Column width={12} textAlign="left" stretched>
                <ValidationError
                  errors={errors}
                  name="confirmUsername"
                  pointing="above"
                  internal={true}
                />
              </Grid.Column>
            </Grid.Row>

            <Grid.Row style={{ marginTop: "2rem" }}>
              <Grid.Column width={8}>
                <Button
                  secondary
                  className={cStyles.buttonSecond}
                  onClick={() => onChangeEmailComplete()}
                >
                  <Ico name="cancel" style={{ marginRight: "10px" }} />
                  {t("common:cancel")}
                </Button>
              </Grid.Column>

              <Grid.Column width={8} floated="right" textAlign="right">
                <Button
                  id="changeEmailButton"
                  primary
                  className={cStyles.buttonPrime}
                >
                  <Ico name="check" style={{ marginRight: "10px" }} />
                  {t("change")}
                </Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Form>
        {message && (
          <ConfirmationDialog
            title={message.title}
            message={message.message}
            onOk={() => {
              setMessage(null);
              if (emailChangeSuccess && !disableTokenRefresh) {
                setLoading(true);
                window.location.reload();
              } else {
                onChangeEmailComplete();
              }
            }}
          />
        )}
      </Modal>
    </Transition>
  );
};

export default ChangeEmailDialog;
